import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from '@/app/App';
import '@/styles/index.css';
import { escucharRedDelSistema } from '@/core/red/redDelSistema';
import { esRutaDeBaja, esRutaDePrivacidad, hayQueElegirContrasena } from '@/app/rutas';

// Antes de dibujar nada. La primera petición que hace la aplicación es la de la
// sesión, y si el teléfono está en modo avión conviene saberlo ANTES de
// esperarla: eso es la diferencia entre abrir al instante con la credencial
// guardada y quedarse unos segundos en «Cargando…». En la web no hace nada.
void escucharRedDelSistema();

const container = document.getElementById('root');
if (!container) throw new Error('No se encontró el elemento #root');

/**
 * La vitrina: las pantallas con datos de mentira, para poder mirarlas sin
 * sesión y en los dos temas. Sólo en desarrollo y sólo en `/vitrina`; el
 * `import()` condicionado deja que el empaquetador la saque del paquete de
 * producción por completo.
 */
const enVitrina = import.meta.env.DEV && window.location.pathname === '/vitrina';
const Vitrina = enVitrina
  ? lazy(() => import('@/app/Vitrina').then((m) => ({ default: m.Vitrina })))
  : null;

/**
 * La baja de cuenta desde la web, en `/baja`.
 *
 * A diferencia de la vitrina, ésta SÍ va en producción: Google Play exige que
 * la cuenta se pueda dar de baja desde una dirección web a la que se llegue sin
 * instalar la aplicación, para quien ya la desinstaló o cambió de teléfono.
 *
 * Se monta acá y no adentro de `App` porque no necesita nada del armazón —ni
 * organización activa, ni período, ni cola de sincronización—, y quien llega
 * puede no tener ninguna de esas cosas. Cargar la aplicación entera para poder
 * abandonarla sería el chiste equivocado.
 *
 * Dentro del teléfono esta rama no se toma nunca: el WebView abre siempre en
 * la raíz.
 */
const BajaWeb = esRutaDeBaja(window.location.pathname)
  ? lazy(() => import('@/app/BajaWeb').then((m) => ({ default: m.BajaWeb })))
  : null;

/**
 * Elegir una contraseña nueva, en `/nueva-contrasena`.
 *
 * Es a donde cae quien toca el enlace del correo de recuperación. Se monta
 * fuera de `App` por lo mismo que la baja: quien llega no tiene organización
 * activa ni período, y puede estar en el navegador de un teléfono prestado.
 *
 * Que vaya primero en la cadena de abajo no es casualidad. El enlace trae la
 * sesión adentro, así que si cayera en `App` la aplicación se abriría normal
 * —con la contraseña vieja todavía como única válida— y la persona creería que
 * ya está, hasta la próxima vez que tenga que entrar.
 *
 * Y se reconoce por DOS cosas: la dirección, y el `type=recovery` que viene en
 * el `#`. La segunda es la que de verdad sostiene esto. A dónde manda el correo
 * lo decide el «Site URL» del panel de Supabase, que puede apuntar a la raíz y
 * de hecho apuntaba: el enlace caía en la aplicación como si nada. Mirando el
 * `#` da igual dónde caiga.
 */
const NuevaContrasena = hayQueElegirContrasena(window.location.pathname, window.location.hash)
  ? lazy(() => import('@/app/NuevaContrasena').then((m) => ({ default: m.NuevaContrasena })))
  : null;

/**
 * La política de privacidad, en `/privacidad`.
 *
 * Va en producción y se dibuja sin el armazón, igual que la baja: quien la abre
 * puede no tener cuenta —es probable que sea alguien revisando la ficha de la
 * tienda— y cargar la aplicación entera para mostrar texto sería pedirle que se
 * registre para leer cómo se van.
 */
const Privacidad = esRutaDePrivacidad(window.location.pathname)
  ? lazy(() => import('@/app/Privacidad').then((m) => ({ default: m.Privacidad })))
  : null;

createRoot(container).render(
  <StrictMode>
    {NuevaContrasena ? (
      <Suspense fallback={null}>
        <NuevaContrasena />
      </Suspense>
    ) : BajaWeb ? (
      <Suspense fallback={null}>
        <BajaWeb />
      </Suspense>
    ) : Privacidad ? (
      <Suspense fallback={null}>
        <Privacidad />
      </Suspense>
    ) : Vitrina ? (
      <Suspense fallback={null}>
        <Vitrina />
      </Suspense>
    ) : (
      <App />
    )}
  </StrictMode>,
);
